import { PublishStatus } from '@src/types.ts/publishRecord';

import type { PublishRecord } from '@src/types.ts/publishRecord';

/**
 * A publish record is "finished" once its render reached a final state —
 * `succeeded` or `failed`. `queued` / `rendering` / `retry_pending` are still in
 * flight. Used to prune the in-flight tracking set and to gate render-completion
 * side-effects.
 */
export const isPublishRecordFinished = (
  record: Pick<PublishRecord, 'publishStatus'>,
): boolean =>
  record.publishStatus === PublishStatus.Succeeded ||
  record.publishStatus === PublishStatus.Failed;

/**
 * A publish record is "in flight" when its render has not yet reached a
 * terminal state — queued, actively rendering, or pending retry.
 */
export const isPublishRecordInFlight = (status: PublishStatus): boolean =>
  status === PublishStatus.Queued ||
  status === PublishStatus.Rendering ||
  status === PublishStatus.RetryPending;

/**
 * How long a `queued` record may sit without the backend accepting its render
 * (no `renderId`) before we treat it as abandoned. Matches the story-upload
 * "stuck" threshold (`isVideoUploadStuck`) — ample headroom over render-accept
 * latency.
 */
export const PUBLISH_ORPHAN_STUCK_TTL_MS = 25 * 60 * 1000;

/**
 * A publish record is a stuck orphan when it was persisted as `queued` but its
 * render was never dispatched/accepted (`renderId` is still null) and it has
 * aged past the TTL. This happens when the client couldn't dispatch after
 * creating the record — offline, a crash/tab-close between create and dispatch,
 * or a non-2xx render response that didn't throw. Keyed on `publishedAt` (set to
 * the creation time on both create paths); a missing/unparseable timestamp
 * returns false so a fresh record is never falsely reaped.
 */
export const isPublishRecordStuck = (
  record: Pick<PublishRecord, 'publishStatus' | 'renderId' | 'publishedAt'>,
  now: number = Date.now(),
): boolean =>
  record.publishStatus === PublishStatus.Queued &&
  !record.renderId &&
  !!record.publishedAt &&
  now - Date.parse(record.publishedAt) > PUBLISH_ORPHAN_STUCK_TTL_MS;
